<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $t('messages.edit') + ': ' + $tc('messages.gamification_chests', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.base')" for="base" />
                            <FormInput
                                required
                                id="base"
                                v-model="form.base"
                                name="base"
                                type="text"
                                class="mt-1 w-full"
                                readonly
                            />
                            <FormError :value="form.errors.base" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.coins_amount_base')" for="coins_amount_base" />
                            <FormInput
                                required
                                id="coins_amount_base"
                                v-model="form.coins_amount_base"
                                name="coins_amount_base"
                                type="number"
                                min="0"
                                max="10000"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.coins_amount_base" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.gamification_chests', 2) }}
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_chest_active')" for="is_chest_active" />
                            <input
                                id="is_chest_active"
                                type="checkbox"
                                v-model="form.is_chest_active"
                                name="is_chest_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_chest_active" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_auto_chest_active_mode')" for="is_auto_chest_active" />
                            <FormRadioGroupCards
                                name="is_reward_evenly_split"
                                v-model="form.is_auto_chest_active"
                                :options="options.autoChestActive"
                            />
                            <FormError :value="form.errors.is_auto_chest_active" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_chest_experience_active')"
                                for="is_chest_experience_active"
                            />
                            <input
                                id="is_chest_experience_active"
                                type="checkbox"
                                v-model="form.is_chest_experience_active"
                                name="is_chest_experience_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_chest_experience_active" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-8">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.chest_experience_amplitude')"
                                for="chest_experience_amplitude"
                            />
                            <div class="flex space-x-2 items-center">
                                <div>+/-</div>
                                <FormInput
                                    required
                                    id="chest_experience_amplitude"
                                    v-model.number="form.chest_experience_amplitude"
                                    min="0"
                                    :max="baseExperienceWithMultipliers"
                                    name="chest_experience_amplitude"
                                    type="number"
                                    class="mt-1"
                                />
                                <div>
                                    [{{ baseExperienceWithMultipliers - form.chest_experience_amplitude }}
                                    -
                                    {{ baseExperienceWithMultipliers + form.chest_experience_amplitude }}]
                                </div>
                            </div>
                            <FormError :value="form.errors.chest_experience_amplitude" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_chest_coins_active')" for="is_chest_coins_active" />
                            <input
                                id="is_chest_coins_active"
                                type="checkbox"
                                v-model="form.is_chest_coins_active"
                                name="is_chest_coins_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_chest_coins_active" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-8">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.chest_coins_amplitude')" for="chest_coins_amplitude" />
                            <div class="flex space-x-2 items-center">
                                <div>+/-</div>
                                <FormInput
                                    required
                                    id="chest_coins_amplitude"
                                    v-model.number="form.chest_coins_amplitude"
                                    min="0"
                                    :max="baseCoinsWithMultipliers"
                                    name="chest_coins_amplitude"
                                    type="number"
                                    class="mt-1"
                                />
                                <div>
                                    [{{ baseCoinsWithMultipliers - form.chest_coins_amplitude }}
                                    -
                                    {{ baseCoinsWithMultipliers + form.chest_coins_amplitude }}]
                                </div>
                            </div>
                            <FormError :value="form.errors.chest_coins_amplitude" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.max_chest_items_per_student')"
                                for="max_chest_items_per_student"
                            />
                            <FormInput
                                required
                                id="max_chest_items_per_student"
                                v-model.number="form.max_chest_items_per_student"
                                name="max_chest_items_per_student"
                                type="number"
                                step="1"
                                min="1"
                                max="20"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.max_chest_items_per_student" class="mt-2" />
                        </div>
                    </div>

                    <details class="mt-3 sm:col-span-12">
                        <summary>
                            <svg
                                class="w-6 h-6 text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </summary>
                        <p v-text="$t('messages.chest_types_explanation')" class="whitespace-pre-line" />
                    </details>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.chest_type_basic', 1) }}

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.active')" for="is_chest_basic_active" />
                                <input
                                    id="is_chest_basic_active"
                                    type="checkbox"
                                    v-model="form.is_chest_basic_active"
                                    name="is_chest_basic_active"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <FormError :value="form.errors.is_chest_basic_active" class="mt-2" />
                            </div>
                        </div>
                    </div>
                    <div class="mt-1 inline-block" v-show="form.is_chest_active" v-if="form.chest_basic_multiplier">
                        <div class="mt-1" v-show="form.is_chest_active">
                            <TreasureChest class="h-40" type="basic" :is-open="form.is_chest_coins_active" />

                            <div class="flex justify-between items-center">
                                <div>
                                    <BadgePill color="green" v-show="form.is_chest_experience_active">
                                        {{
                                            Math.floor(
                                                (baseExperienceWithMultipliers - form.chest_experience_amplitude) *
                                                    form.chest_basic_multiplier,
                                            )
                                        }}
                                        -
                                        {{
                                            Math.floor(
                                                (baseExperienceWithMultipliers + form.chest_experience_amplitude) *
                                                    form.chest_basic_multiplier,
                                            )
                                        }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <BadgePill color="yellow" v-show="form.is_chest_coins_active">
                                        {{
                                            Math.floor(
                                                (baseCoinsWithMultipliers - form.chest_coins_amplitude) *
                                                    form.chest_basic_multiplier,
                                            )
                                        }}
                                        -
                                        {{
                                            Math.floor(
                                                (baseCoinsWithMultipliers + form.chest_coins_amplitude) *
                                                    form.chest_basic_multiplier,
                                            )
                                        }}
                                    </BadgePill>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6" :class="{ 'opacity-50': !form.is_chest_basic_active }">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.multiplier')" for="chest_basic_multiplier" />
                            <FormInput
                                required
                                id="chest_basic_multiplier"
                                v-model.number="form.chest_basic_multiplier"
                                name="chest_basic_multiplier"
                                type="number"
                                step="0.01"
                                min="0.01"
                                max="1"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.chest_basic_multiplier" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <ChestItemsTable
                                type="basic"
                                v-model="form.chestItems.basic"
                                :gamification-items="options.items"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <hr />

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.chest_type_default', 1) }}
                    </div>
                    <div class="mt-1 inline-block" v-show="form.is_chest_active">
                        <TreasureChest class="h-40" :is-open="form.is_chest_coins_active" />

                        <div class="mt-1" v-show="form.is_chest_active">
                            <div class="flex justify-between items-center">
                                <div>
                                    <BadgePill color="green" v-show="form.is_chest_experience_active">
                                        {{ baseExperienceWithMultipliers - form.chest_experience_amplitude }}
                                        -
                                        {{ baseExperienceWithMultipliers + form.chest_experience_amplitude }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <BadgePill color="yellow" v-show="form.is_chest_coins_active">
                                        {{ baseCoinsWithMultipliers - form.chest_coins_amplitude }}
                                        -
                                        {{ baseCoinsWithMultipliers + form.chest_coins_amplitude }}
                                    </BadgePill>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6" :class="{ 'opacity-50': !form.is_chest_active }">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <ChestItemsTable
                                type="default"
                                v-model="form.chestItems.default"
                                :gamification-items="options.items"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <hr />

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.chest_type_special', 1) }}
                    </div>
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.active')" for="is_chest_special_active" />
                            <input
                                id="is_chest_special_active"
                                type="checkbox"
                                v-model="form.is_chest_special_active"
                                name="is_chest_special_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_chest_special_active" class="mt-2" />
                        </div>
                    </div>
                    <div class="mt-1 inline-block" v-show="form.is_chest_active" v-if="form.chest_special_multiplier">
                        <TreasureChest class="h-40" type="special" :is-open="form.is_chest_coins_active" />

                        <div class="mt-1" v-show="form.is_chest_active">
                            <div class="flex justify-between items-center">
                                <div>
                                    <BadgePill color="green" v-show="form.is_chest_experience_active">
                                        {{
                                            Math.floor(
                                                (baseExperienceWithMultipliers - form.chest_experience_amplitude) *
                                                    form.chest_special_multiplier,
                                            )
                                        }}
                                        -
                                        {{
                                            Math.floor(
                                                (baseExperienceWithMultipliers + form.chest_experience_amplitude) *
                                                    form.chest_special_multiplier,
                                            )
                                        }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <BadgePill color="yellow" v-show="form.is_chest_coins_active">
                                        {{
                                            Math.floor(
                                                (baseCoinsWithMultipliers - form.chest_coins_amplitude) *
                                                    form.chest_special_multiplier,
                                            )
                                        }}
                                        -
                                        {{
                                            Math.floor(
                                                (baseCoinsWithMultipliers + form.chest_coins_amplitude) *
                                                    form.chest_special_multiplier,
                                            )
                                        }}
                                    </BadgePill>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6" :class="{ 'opacity-50': !form.is_chest_special_active }">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.multiplier')" for="chest_special_multiplier" />
                            <FormInput
                                required
                                id="chest_special_multiplier"
                                v-model.number="form.chest_special_multiplier"
                                name="chest_catch_up_percent"
                                type="number"
                                step="0.1"
                                min="1"
                                max="10"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.chest_special_multiplier" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <ChestItemsTable
                                type="special"
                                v-model="form.chestItems.special"
                                :gamification-items="options.items"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <hr />

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.chest_type_catch_up', 1) }}
                    </div>
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.active')" for="is_chest_catch_up_active" />
                            <input
                                id="is_chest_catch_up_active"
                                type="checkbox"
                                v-model="form.is_chest_catch_up_active"
                                name="is_chest_catch_up_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_chest_catch_up_active" class="mt-2" />
                        </div>
                    </div>
                    <div class="mt-1 inline-block" v-show="form.is_chest_active" v-if="form.chest_special_multiplier">
                        <TreasureChest class="h-40" type="special" :is-open="form.is_chest_coins_active" />

                        <div class="mt-1" v-show="form.is_chest_active">
                            <div class="flex justify-between items-center">
                                <div>
                                    <BadgePill color="green" v-show="form.is_chest_experience_active">
                                        {{
                                            Math.floor(
                                                (baseExperienceWithMultipliers - form.chest_experience_amplitude) *
                                                    form.chest_special_multiplier,
                                            )
                                        }}
                                        -
                                        {{
                                            Math.floor(
                                                (baseExperienceWithMultipliers + form.chest_experience_amplitude) *
                                                    form.chest_special_multiplier,
                                            )
                                        }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <BadgePill color="yellow" v-show="form.is_chest_coins_active">
                                        {{
                                            Math.floor(
                                                (baseCoinsWithMultipliers - form.chest_coins_amplitude) *
                                                    form.chest_special_multiplier,
                                            )
                                        }}
                                        -
                                        {{
                                            Math.floor(
                                                (baseCoinsWithMultipliers + form.chest_coins_amplitude) *
                                                    form.chest_special_multiplier,
                                            )
                                        }}
                                    </BadgePill>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="md:grid md:grid-cols-12 md:gap-6" :class="{ 'opacity-50': !form.is_chest_catch_up_active }">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.chest_catch_up_percent')" for="chest_catch_up_percent" />
                            <FormInput
                                required
                                id="chest_catch_up_percent"
                                v-model.number="form.chest_catch_up_percent"
                                name="chest_catch_up_percent"
                                type="number"
                                step="1"
                                min="0"
                                max="100"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.chest_catch_up_percent" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12" v-if="form.chest_catch_up_percent">
                        <div class="mt-1">
                            <CatchUpPercentExplainer
                                :groups="gamificationSet.groupsExperiences"
                                :percent="form.chest_catch_up_percent"
                            />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <ChestItemsTable
                                type="catch_up"
                                v-model="form.chestItems.catch_up"
                                :gamification-items="options.items"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import BadgePill from '@/components/BadgePill.vue';

import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppButton from '@/components/AppButton.vue';
import ChestItemsTable from '@/components/ChestItemsTable.vue';
import CatchUpPercentExplainer from '@/components/pageSpecific/CatchUpPercentExplainer.vue';
import TreasureChest from '@/components/TreasureChest.vue';
import FormRadioGroupCards from '@/components/Forms/FormRadioGroupCards.vue';

export default {
    components: {
        FormRadioGroupCards,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        BadgePill,
        CatchUpPercentExplainer,
        ChestItemsTable,
        AppButton,
        TreasureChest,
    },
    name: 'Edit',
    layout: Layout,
    props: ['meta', 'options', 'gamificationSet'],
    data() {
        return {
            form: this.$inertia.form({
                title: this.gamificationSet.title,
                base: this.gamificationSet.base,
                ratio: this.gamificationSet.ratio,
                health_max: this.gamificationSet.health_max,
                show_level: this.gamificationSet.show_level,
                show_experience: this.gamificationSet.show_experience,
                show_coins: this.gamificationSet.show_coins,
                use_health: this.gamificationSet.use_health,
                use_experience: this.gamificationSet.use_experience,
                use_coins: this.gamificationSet.use_coins,

                students_can_upload_avatars: this.gamificationSet.students_can_upload_avatars,

                percent_health_loss_foul_mild: this.gamificationSet.percent_health_loss_foul_mild,
                percent_health_loss_foul_serious: this.gamificationSet.percent_health_loss_foul_serious,
                percent_health_loss_foul_very_serious: this.gamificationSet.percent_health_loss_foul_very_serious,

                coins_amount_base: this.gamificationSet.coins_amount_base,
                is_coins_for_activity_active: this.gamificationSet.is_coins_for_activity_active,
                is_coins_for_activity_reward_proportional:
                    this.gamificationSet.is_coins_for_activity_reward_proportional,
                is_experience_for_activity_active: this.gamificationSet.is_experience_for_activity_active,
                is_experience_for_activity_reward_proportional:
                    this.gamificationSet.is_experience_for_activity_reward_proportional,
                students_currency_graph_days_span: this.gamificationSet.students_currency_graph_days_span,
                percent_health_recovery_attendance: this.gamificationSet.percent_health_recovery_attendance,
                percent_health_recovery_delay: this.gamificationSet.percent_health_recovery_delay,
                percent_health_recovery_unjustified_absence:
                    this.gamificationSet.percent_health_recovery_unjustified_absence,
                is_trimester_accelerator_active: this.gamificationSet.is_trimester_accelerator_active,
                is_trimester_accelerator_applied_to_rewards:
                    this.gamificationSet.is_trimester_accelerator_applied_to_rewards,
                game_speed: this.gamificationSet.game_speed,
                is_game_speed_applied_to_rewards: this.gamificationSet.is_game_speed_applied_to_rewards,
                is_chest_active: this.gamificationSet.is_chest_active,
                is_chest_coins_active: this.gamificationSet.is_chest_coins_active,
                chest_coins_amplitude: this.gamificationSet.chest_coins_amplitude,
                is_chest_experience_active: this.gamificationSet.is_chest_experience_active,
                is_auto_chest_active: this.gamificationSet.is_auto_chest_active,
                chest_experience_amplitude: this.gamificationSet.chest_experience_amplitude,
                chest_catch_up_percent: this.gamificationSet.chest_catch_up_percent,
                weekly_reward_coins_multiplier: this.gamificationSet.weekly_reward_coins_multiplier,
                weekly_reward_experience_multiplier: this.gamificationSet.weekly_reward_experience_multiplier,
                chestItems: this.gamificationSet.chestItems,
                chest_basic_multiplier: this.gamificationSet.chest_basic_multiplier,
                chest_special_multiplier: this.gamificationSet.chest_special_multiplier,
                max_chest_items_per_student: this.gamificationSet.max_chest_items_per_student,
                is_chest_basic_active: this.gamificationSet.is_chest_basic_active,
                is_chest_special_active: this.gamificationSet.is_chest_special_active,
                is_chest_catch_up_active: this.gamificationSet.is_chest_catch_up_active,
            }),
        };
    },
    methods: {
        submit() {
            this.form
                .transform((data) => {
                    const chestItems = [];
                    for (const [, values] of Object.entries(data.chestItems)) {
                        values.forEach((value) => {
                            chestItems.push(value);
                        });
                    }
                    data.chestItems = chestItems;
                    return data;
                })
                .patch(this.meta.links.update);
        },
        deleteChestItemRow(index) {
            this.form.chestItems.splice(index, 1);
        },
        addChestItemRow() {
            this.form.chestItems.push({
                gamification_item_id: null,
                probability_to_obtain: 0,
                is_always_given: false,
            });
        },
    },
    computed: {
        weeklyRewardExperience: function () {
            return Math.round(this.baseExperienceWithMultipliers * this.form.weekly_reward_experience_multiplier);
        },
        weeklyRewardCoins: function () {
            return Math.round(this.baseCoinsWithMultipliers * this.form.weekly_reward_coins_multiplier);
        },
        baseExperienceWithMultipliers: function () {
            const trimester_speed = 1.2;
            return Math.round(this.form.base * this.form.game_speed * trimester_speed); //TODO: get trimester from settings
        },
        baseCoinsWithMultipliers: function () {
            const trimester_speed = 1.2;
            return Math.round(this.form.coins_amount_base * this.form.game_speed * trimester_speed); //TODO: get trimester from settings
        },
        chestImage() {
            return this.form.is_chest_coins_active
                ? '/img/gamification/chest_open.svg'
                : '/img/gamification/chest_closed.svg';
        },
    },
};
</script>
<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
